/**
 * EnterpriseHumanCoaching.jsx
 * ============================
 * 
 * Configuration et achat d'un programme de coaching humain entreprise.
 * Le RH définit : nom du programme, nb sessions, durée, budget max/h, thématiques.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import {
  Users, Check, ArrowRight, Loader2, Euro, Clock, Target, Building2, X
} from 'lucide-react';

const API = getApiUrl();

const SPECIALTIES_OPTIONS = [
  'Leadership', 'Management', 'Communication', 'Gestion du stress',
  'Prise de parole', 'Intelligence emotionnelle', 'Gestion de conflits',
  'Transition de carriere', 'Performance', 'Organisation',
  'Coaching d\'equipe', 'Developpement personnel',
];

export default function EnterpriseHumanCoaching() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [form, setForm] = useState({
    program_name: '',
    company_name: '',
    billing_address: '',
    vat_number: '',
    contact_email: '',
    total_sessions: 20,
    session_duration_minutes: 60,
    max_rate_per_hour: 150,
    specialties_focus: [],
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Calculate price estimate locally (will be confirmed by backend)
  const estimatedTotal = form.total_sessions * form.max_rate_per_hour * (form.session_duration_minutes / 60);

  const toggleSpecialty = (s) => {
    setForm(prev => ({
      ...prev,
      specialties_focus: prev.specialties_focus.includes(s)
        ? prev.specialties_focus.filter(x => x !== s)
        : [...prev.specialties_focus, s],
    }));
  };

  const handleSubmit = async () => {
    if (!form.program_name || !form.company_name || !form.contact_email || form.total_sessions < 1 || form.max_rate_per_hour < 1) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!user) {
      toast.error('Veuillez vous connecter');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/coaching-programs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...form,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success('Programme créé');
        navigate('/company/coaching-humain');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="theme-light min-h-screen pb-28 sm:pb-12">
      {/* Header */}
      <div className="border-b border-[#E8E5DE]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-8">
          <BackButton />
          <p className="uppercase text-xs tracking-[0.25em] text-green-400 mb-3 mt-4">Entreprise — Coaching Humain</p>
          <h1 className="text-2xl sm:text-3xl font-light text-[#111827] tracking-tight mb-2">
            Configurez votre programme
          </h1>
          <p className="text-[#6B7280] text-sm leading-relaxed">
            Definissez le cadre du coaching pour vos collaborateurs. Apres paiement, vous pourrez importer vos equipes et attribuer les sessions.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="space-y-8">

          {/* Program name */}
          <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
            <label className="text-sm font-medium text-[#4B5563] mb-2 block">Nom du programme *</label>
            <Input data-testid="program-name" value={form.program_name}
              onChange={(e) => setForm({ ...form, program_name: e.target.value })}
              placeholder="Ex: Programme Leadership Q2 2026"
              className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
          </div>

          {/* Company info */}
          <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
            <h3 className="text-[#111827] font-medium mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#D4AF37]" /> Informations entreprise
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#6B7280] mb-1.5 block">Nom de l'entreprise *</label>
                <Input data-testid="company-name" value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Ex: Acme Corp"
                  className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] mb-1.5 block">E-mail de contact *</label>
                <Input data-testid="contact-email" type="email" value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  placeholder="rh@entreprise.com"
                  className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-[#6B7280] mb-1.5 block">Adresse de facturation *</label>
                <Input data-testid="billing-address" value={form.billing_address}
                  onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                  placeholder="12 rue de la Paix, 75002 Paris"
                  className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] mb-1.5 block">Numero de TVA</label>
                <Input data-testid="vat-number" value={form.vat_number}
                  onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                  placeholder="FR12345678901"
                  className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
              </div>
            </div>
          </div>

          {/* Sessions config */}
          <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
            <h3 className="text-[#111827] font-medium mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#D4AF37]" /> Configuration des sessions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-[#6B7280] mb-1.5 block">Nombre total de sessions *</label>
                <Input data-testid="total-sessions" type="number" value={form.total_sessions}
                  onChange={(e) => setForm({ ...form, total_sessions: parseInt(e.target.value) || '' })}
                  className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" min="1" placeholder="20" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] mb-1.5 block">Duree par session (min)</label>
                <Input data-testid="session-duration" type="number" value={form.session_duration_minutes}
                  onChange={(e) => setForm({ ...form, session_duration_minutes: parseInt(e.target.value) || '' })}
                  className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" min="30" step="15" placeholder="60" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] mb-1.5 block">Tarif max / heure (EUR) *</label>
                <Input data-testid="max-rate" type="number" value={form.max_rate_per_hour}
                  onChange={(e) => setForm({ ...form, max_rate_per_hour: parseFloat(e.target.value) || '' })}
                  className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" min="1" placeholder="150" />
              </div>
            </div>
          </div>

          {/* Specialties focus */}
          <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
            <h3 className="text-[#111827] font-medium mb-2 flex items-center gap-2">
              <Target className="w-4 h-4 text-[#D4AF37]" /> Thematiques cibles
            </h3>
            <p className="text-[#6B7280] text-xs mb-4">Selectionnez les axes prioritaires. L'IA orientera les collaborateurs vers les coachs specialises.</p>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES_OPTIONS.map(s => (
                <button key={s} onClick={() => toggleSpecialty(s)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                    form.specialties_focus.includes(s)
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'bg-white/5 text-[#6B7280] border border-transparent hover:text-[#111827] hover:bg-white/10'
                  }`}>
                  {form.specialties_focus.includes(s) ? <Check className="w-3 h-3 inline mr-1" /> : '+ '}
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
            <label className="text-sm font-medium text-[#4B5563] mb-2 block">Notes complementaires</label>
            <Textarea data-testid="program-notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Contexte specifique, objectifs, contraintes..."
              className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl min-h-[80px]" />
          </div>

          {/* Price summary */}
          <div className="bg-white border border-green-500/20 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#111827] font-medium flex items-center gap-2">
                <Euro className="w-4 h-4 text-green-400" /> Estimation budgetaire
              </h3>
              <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Budget maximum</Badge>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-light text-[#111827]">{form.total_sessions || 0}</p>
                <p className="text-[#6B7280] text-xs">sessions</p>
              </div>
              <div>
                <p className="text-2xl font-light text-[#111827]">{form.session_duration_minutes || 0}<span className="text-sm text-[#6B7280]"> min</span></p>
                <p className="text-[#6B7280] text-xs">par session</p>
              </div>
              <div>
                <p className="text-2xl font-light text-green-400">{isNaN(estimatedTotal) ? '—' : `${estimatedTotal.toLocaleString('fr-FR')}€`}</p>
                <p className="text-[#6B7280] text-xs">budget max</p>
              </div>
            </div>
            <p className="text-[#6B7280] text-xs mt-3">Le montant reel depend du tarif de chaque coach. Seules les sessions validees sont debitees.</p>
          </div>

          {/* Submit */}
          <button data-testid="submit-program" onClick={handleSubmit} disabled={submitting}
            className="w-full py-4 bg-green-500 text-[#111827] rounded-full font-medium text-sm hover:bg-green-400 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Building2 className="w-4 h-4" />
                Creer le programme et payer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
